import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

import type { ButtonTokenSections } from '@primeuix/themes/types/button';
import type { DataTableTokenSections } from '@primeuix/themes/types/datatable';
import type { MessageTokenSections } from '@primeuix/themes/types/message';
import type { BadgeTokenSections } from '@primeuix/themes/types/badge';
import type { ToggleSwitchTokenSections } from '@primeuix/themes/types/toggleswitch';
import type { FieldsetTokenSections } from '@primeuix/themes/types/fieldset';
import type { TabsTokenSections } from '@primeuix/themes/types/tabs';
import type { CheckboxTokenSections } from '@primeuix/themes/types/checkbox';

const checkboxRoot: CheckboxTokenSections.Root = {
  borderRadius: '{border.radius.sm}',
  width: '1.25rem',
  height: '1.25rem',
  background: '{form.field.background}',
  checkedBackground: '{primary.700}',
  checkedHoverBackground: '{primary.hover.color}',
  disabledBackground: '{form.field.disabled.background}',
  filledBackground: '{form.field.filled.background}',
  borderColor: '{form.field.border.color}',
  hoverBorderColor: '{form.field.hover.border.color}',
  focusBorderColor: '{form.field.border.color}',
  checkedBorderColor: '{primary.color}',
  checkedHoverBorderColor: '{primary.hover.color}',
  checkedFocusBorderColor: '{primary.color}',
  checkedDisabledBorderColor: '{form.field.border.color}',
  invalidBorderColor: '{form.field.invalid.border.color}',
  shadow: '{form.field.shadow}',
  focusRing: {
    width: '{focus.ring.width}',
    style: '{focus.ring.style}',
    color: '{focus.ring.color}',
    offset: '{focus.ring.offset}',
    shadow: '{focus.ring.shadow}',
  },
  transitionDuration: '{form.field.transition.duration}',
  sm: {
    width: '1rem',
    height: '1rem',
  },
  lg: {
    width: '1.5rem',
    height: '1.5rem',
  },
};

const tab: TabsTokenSections.Tab = {
  background: 'transparent',
  hoverBackground: 'transparent',
  activeBackground: 'transparent',
  borderWidth: '0 0 1px 0',
  borderColor: '{content.border.color}',
  hoverBorderColor: '{content.border.color}',
  activeBorderColor: '{primary.700}',
  color: '{text.muted.color}',
  hoverColor: '{text.color}',
  activeColor: '{primary.700}',
  padding: '1rem 1.125rem',
  fontWeight: '600',
  margin: '0 0 -1px 0',
  gap: '0.5rem',
  focusRing: {
    width: '{focus.ring.width}',
    style: '{focus.ring.style}',
    color: '{focus.ring.color}',
    offset: '-1px',
    shadow: '{focus.ring.shadow}',
  },
};

const fieldsetRoot: FieldsetTokenSections.Root = {
  background: '{content.background}',
  borderColor: '{slate.300}',
  borderRadius: '7px',
  color: '{content.color}',
  padding: '0 1rem 1rem 1rem',
  transitionDuration: '{transition.duration}',
};

const fieldsetContent: FieldsetTokenSections.Content = {
  padding: '0 0.5rem',
};

const toggleSwitchColorScheme: ToggleSwitchTokenSections.ColorScheme = {
  light: {
    root: {
      background: '{slate.300}',
      disabledBackground: '{form.field.disabled.background}',
      hoverBackground: '{primary.300}',
      checkedBackground: '{primary.600}',
      checkedHoverBackground: '{primary.800}',
    },
  },
};

const badgeColorScheme: BadgeTokenSections.ColorScheme = {
  light: {
    primary: {
      background: '{primary.700}',
      color: '{primary.50}',
    },
  },
};

const messageColorScheme: MessageTokenSections.ColorScheme = {
  light: {
    info: {
      background: '{primary.50}',
      borderColor: '{primary.200}',
      color: '{primary.800}',
      closeButton: {
        hoverBackground: '{primary.100}',
        focusRing: {
          color: '{primary.600}',
          shadow: 'none',
        },
      },
      outlined: {
        color: '{primary.600}',
        borderColor: '{primary.600}',
      },
      simple: {
        color: '{primary.600}',
      },
    },
    warn: {
      background: '{warn.50}',
      borderColor: '{warn.400}',
      color: '{warn.700}',
      closeButton: {
        hoverBackground: '{warn.100}',
        focusRing: {
          color: '{warn.600}',
          shadow: 'none',
        },
      },
      outlined: {
        color: '{warn.600}',
        borderColor: '{warn.600}',
      },
      simple: {
        color: '{warn.600}',
      },
    },
    error: {
      background: '{error.50}',
      borderColor: '{error.200}',
      color: '{error.800}',
      closeButton: {
        hoverBackground: '{error.100}',
        focusRing: {
          color: '{error.600}',
          shadow: 'none',
        },
      },
      outlined: {
        color: '{error.600}',
        borderColor: '{error.600}',
      },
      simple: {
        color: '{error.600}',
      },
    },
  },
};

const buttonRoot: ButtonTokenSections.Root = {
  gap: '0.25rem',
};

const buttonColorSchemeTokens: ButtonTokenSections.ColorScheme = {
  light: {
    root: {
      primary: {
        color: '{primary.900}',
        background: '{primary.100}',
        hoverBackground: '{primary.200}',
        hoverColor: '{primary.950}',
        activeBackground: '{primary.300}',
        activeColor: '{primary.950}',
        borderColor: '{primary.600}',
      },
      secondary: {
        color: '{primary.800}',
        background: '{slate.50}',
        hoverBackground: '{primary.100}',
        hoverColor: '{slate.900}',
        activeBackground: '{slate.200}',
        activeColor: '{slate.950}',
        borderColor: '{slate.300}',
        hoverBorderColor: '{slate.400}',
      },
      success: {
        color: '{success.900}',
        background: '{success.100}',
        hoverBackground: '{success.200}',
        hoverColor: '{success.950}',
        activeBackground: '{success.300}',
        activeColor: '{success.950}',
        borderColor: '{success.700}',
      },
      danger: {
        color: '{danger.800}',
        background: '{danger.50}',
        hoverBackground: '{danger.100}',
        hoverColor: '{danger.900}',
        activeBackground: '{danger.200}',
        activeColor: '{danger.900}',
        borderColor: '{danger.700}',
      },
    },
    text: {
      primary: {
        hoverBackground: '{primary.50}',
        activeBackground: '{primary.100}',
        color: '{primary.600}',
      },
      secondary: {
        hoverBackground: '{surface.50}',
        activeBackground: '{surface.100}',
        color: '{surface.500}',
      },
      success: {
        hoverBackground: '{success.50}',
        activeBackground: '{success.100}',
        color: '{success.500}',
      },
      info: {
        hoverBackground: '{sky.50}',
        activeBackground: '{sky.100}',
        color: '{sky.600}',
      },
      warn: {
        hoverBackground: '{warn.50}',
        activeBackground: '{warn.100}',
        color: '{warn.600}',
      },
      danger: {
        hoverBackground: '{danger.50}',
        activeBackground: '{danger.100}',
        color: '{danger.600}',
      },
    },
  },
};

const datatableHeaderCellTokens: DataTableTokenSections.HeaderCell = {
  hoverBackground: '{sky.100}',
  borderColor: '{slate.300}',
  selectedColor: '{primary.900}',
};

export const ManagePreset = definePreset(Aura, {
  semantic: {
    borderRadius: {
      none: '0',
      xs: '3px',
      sm: '5px',
      md: '7px',
      lg: '9px',
      xl: '13px',
    },
    fontSize: {
      xs: '10px',
      sm: '12px',
      base: '14px',
      md: '16px',
      lg: '18px',
      xl: '20px',
      xxl: '24px',
      xxxl: '28px',
      huge: '34px',
    },
    primary: {
      50: '{sky.50}',
      100: '{sky.100}',
      200: '{sky.200}',
      300: '{sky.300}',
      400: '{sky.400}',
      500: '{sky.500}',
      600: '{sky.600}',
      700: '{sky.700}',
      800: '{sky.800}',
      900: '{sky.900}',
      950: '{sky.950}',
    },
    success: {
      50: '{green.50}',
      100: '{green.100}',
      200: '{green.200}',
      300: '{green.300}',
      400: '{green.400}',
      500: '{green.500}',
      600: '{green.600}',
      700: '{green.700}',
      800: '{green.800}',
      900: '{green.900}',
      950: '{green.950}',
    },
    warn: {
      50: '{orange.50}',
      100: '{orange.100}',
      200: '{orange.200}',
      300: '{orange.300}',
      400: '{orange.400}',
      500: '{orange.500}',
      600: '{orange.600}',
      700: '{orange.700}',
      800: '{orange.800}',
      900: '{orange.900}',
      950: '{orange.950}',
    },
    error: {
      50: '{red.50}',
      100: '{red.100}',
      200: '{red.200}',
      300: '{red.300}',
      400: '{red.400}',
      500: '{red.500}',
      600: '{red.600}',
      700: '{red.700}',
      800: '{red.800}',
      900: '{red.900}',
      950: '{red.950}',
    },
    danger: {
      50: '{red.50}',
      100: '{red.100}',
      200: '{red.200}',
      300: '{red.300}',
      400: '{red.400}',
      500: '{red.500}',
      600: '{red.600}',
      700: '{red.700}',
      800: '{red.800}',
      900: '{red.900}',
      950: '{red.950}',
    },
    colorScheme: {
      light: {
        button: {
          primary: {
            background: '{orange.500}',
          },
        },
      },
    },
  },
  components: {
    button: {
      root: buttonRoot,
      colorScheme: buttonColorSchemeTokens,
    },
    message: {
      colorScheme: messageColorScheme,
    },
    badge: {
      colorScheme: badgeColorScheme,
    },
    toggleswitch: {
      colorScheme: toggleSwitchColorScheme,
    },
    fieldset: {
      root: fieldsetRoot,
      content: fieldsetContent,
    },
    tabs: {
      tab: tab,
    },
    datatable: {
      colorScheme: {
        light: {
          headerCell: datatableHeaderCellTokens,
        },
      },
    },
    checkbox: {
      root: checkboxRoot,
    },
  },
});
